import React, { useState, useCallback, useEffect } from 'react';
import { animated, useSpring, useTransition } from '@react-spring/web';
import { nanoid } from 'nanoid';
import cx from 'classnames';
import { findLast, map, size } from 'lodash';

import Header from './components/header';
import ActionBar from './components/actionBar';
import History from './components/history';
import Chats from './components/chats';

import { useDealChatsFetch, useCurrentDealChatStream, useClearDealChats, useFetchSessionChats } from 'src/query/dealInsights/dealAgent';
import { DealAgentHighlight } from 'src/types/dealAgent';
import styles from './styles.module.scss';
import Highlights from './components/highLights';
import { globalAgentSuggestion } from 'src/utils/dealAgent.helper';

export interface DealAgentProps {
    dealId?: string;
    loadingSummary?: boolean;
    summary?: Record<string, any>;
}

const Placeholder = () => {
    return (<div className={styles.placeholderContainer}>
        <div className={cx(styles.placeholder, styles.lg)}>
            <span className={cx(styles.line, styles.block)} />
        </div>
        <div className={cx(styles.placeholder, styles.md)}>
            <span className={cx(styles.line, styles.block)} />
        </div>
        <div className={cx(styles.placeholder, styles.sm)}>
            <span className={cx(styles.line, styles.block)} />
        </div>
    </div>)
};

const DealAgent: React.FC<DealAgentProps> = ({ dealId, loadingSummary, summary,}) => {
    const [query, setQuery] = useState('');
    const [isHistoryView, setIsHistoryView] = useState(false);
    const { data: chats = [] } = useDealChatsFetch(dealId);
    const fetchSessionChats = useFetchSessionChats(dealId);
    const clearChats = useClearDealChats(); // clear the chats for the specific deal
    const streamChatMutation = useCurrentDealChatStream(dealId);
    const isSummaryView = size(chats) === 0 && !fetchSessionChats.isPending && dealId;
    const isChatLoading = chats.some((chat) => chat.creating);
    const [expandChat, setExpandChat] = useState(false); // expand the chat panel

    useEffect(() => { // whenever component unmounts, clear the chats
        return () => {
            clearChats(dealId ?? '');
        }
    },[dealId]);
    
    // Transitions
    const summaryTransition = useTransition(isSummaryView, {
        initial: null,
        from: (showSummary) => ({
            opacity: 0,
            transform: showSummary ? 'translateY(-18px)' : 'translateY(18px)',
        }),
    
        enter: {
            opacity: 1,
            transform: 'translateY(0px)',
        },
    
        leave: (showSummary) => ({
            opacity: 0,
            transform: showSummary ? 'translateY(-18px)' : 'translateY(18px)', 
        }),
    
        config: {
            tension: 300,
            friction: 24,
        },
    
        exitBeforeEnter: false,  
    });

    const contentTransition = useTransition(isHistoryView ? 'history' : 'main', {
        initial: null,
        from: (key) => ({
                opacity: 0,
                transform: key === 'history' ? 'translateX(100%)' : 'translateX(-100%)',
        }),
        enter: {
                opacity: 1,
                transform: 'translateX(0%)',
        },
        leave: (key) => ({
                opacity: 0,
                transform: key === 'history' ? 'translateX(100%)' : 'translateX(-100%)',
        }),
        config: {
                tension: 240,
                friction: 32,
        },
        exitBeforeEnter: false,
    });

    const actionBarTransition = useTransition(!isHistoryView, {
        initial: null,
        from: { opacity: 0, transform: 'translateY(100%)' },
        enter: { opacity: 1, transform: 'translateY(0%)' },
        leave: { opacity: 0, transform: 'translateY(100%)' },
        config: { tension: 260, friction: 28 },
        delay: 0,
    });

    const expandChatTransition = useSpring({ // transition for the global chat view
        height: expandChat && !dealId ? 534 : 120,
        opacity: expandChat && !dealId ? 1 : 0.98,
        config: {
            mass: 1.2,
            tension: 120,
            friction: 20,
        },
    });

    // Methods
    const handleSubmit = (q?: string) => {
        const question = q ? q : query.trim();
        if (!question || streamChatMutation.isPending) return;

        const chatId = nanoid(6); // generate a unique chat id

        // Find last completed chat that has a session
        const lastAnswered: any = findLast([...chats], (c: any) => c.sessionId && !c.creating);
        const sessionId = lastAnswered?.sessionId ?? '';

        streamChatMutation.mutate({
            chatId,
            question,
            sessionId: sessionId,
        });

        if(!dealId && !expandChat) { // if global chat, expand the chat panel
            setExpandChat(true);
        }
        setQuery(''); // reset the query
    };

    const handleQueryChange = useCallback((nextQuery: string) => {
        setQuery(nextQuery);
    }, []);

    const handleConversationClick = (id: string) => {
        fetchSessionChats.mutate(id); // fetch the chats for the specific sessions
        setIsHistoryView(false); // close
    };

    const handleToggleExpandChat = () => {
        if(expandChat) { // if the chat is expanded, clear the chats
            clearChats();
        }
        setExpandChat(!expandChat);
    };

    return (
        <div className={styles.wrapper}>
            <Header
                isHistory={isHistoryView}
                showSummarize={size(chats) > 0}
                showExpandChat={size(chats) > 0}
                dealId={dealId}
                isChatLoading={isChatLoading || loadingSummary}
                onExitHistory={() => setIsHistoryView(false)}
                onOpenHistory={() => setIsHistoryView(true)}
                toggleSummary={() => clearChats(dealId ?? '')} // toggle the summary (clear the chats for the specific deal)
                onExpandChat={handleToggleExpandChat}
            />
            
            {loadingSummary && dealId ? <Placeholder /> :
               <>
              <div className={styles.chatPanel}>
                {!dealId ? (
                    <animated.div
                    style={expandChatTransition}
                    className={styles.globalChatView}
                >
                {expandChat ? ( <Chats chats={chats} loadingChats={fetchSessionChats.isPending} />
                    ) : (
                    <div className={styles.suggestions}> 
                        {map(globalAgentSuggestion, (pill, index) => (
                            <p key={index} className={styles.pill} onClick={() => handleSubmit(pill.label)}> {pill.label}</p>
                        ))}
                    </div>)}
                </animated.div>
                ) : ( contentTransition((springStyle, view) => (
                            <animated.div
                                style={springStyle}
                                className={styles.historyView}
                            >
                                {view === 'history'  ? <History
                                        dealId={dealId ?? ''}
                                        onConversationClick={handleConversationClick}
                                    />
                                :  summaryTransition((style, showSummary) => (
                                            <animated.div
                                                style={style}
                                                className={styles.chatView}
                                            >
                                                {showSummary ? (
                                                    <Summarize description={summary?.summary ?? ''} highlights={summary?.highlights ?? []} />
                                                ) : <Chats chats={chats} loadingChats={fetchSessionChats.isPending} />}
                                            </animated.div>
                                        ))
                                }
                            </animated.div>
                )))}
            </div>

            {actionBarTransition((springStyle, show) =>
                        show ? ( <animated.div style={{ ...springStyle, transformOrigin: 'bottom center' }} className={styles.actionBar}>
                                <ActionBar
                                    query={query}
                                    onQueryChange={handleQueryChange}
                                    onSubmit={handleSubmit}
                                    disabled={streamChatMutation.isPending}
                                    inputPlaceholder={dealId ? size(chats) === 0  ? "Ask anything about this deal..." : "Ask a follow-up about this deal..." : size(chats) === 0 ? "Ask anything about deals..." : "Ask a follow-up about deals..."}
                                />
                                {(dealId || expandChat) && (
                                    <div className={styles.bottomOverlay} />
                                )}
                            </animated.div>
                        ) : null
            )}


           
           </> }
          </div> 
    );
};

// Summarize
const Summarize = ({ description, highlights }: { description: string, highlights: DealAgentHighlight[] }) => {
   return (
      <div className={styles.summaryContainer}>
          <h6 className={styles.title}>Summary about this deal</h6>
          <p className={styles.desc}>{description ?? '-'}</p>
          <Highlights highlights={highlights} />
      </div>
   )
};

export default DealAgent;
